/**
 * Vehicles API service
 * Provides functions to interact with the vehicles API endpoints
 */

import { api } from './api';
import type { PaginatedResponse } from './api';

/**
 * Vehicle entity from API
 */
export interface Vehicle {
  id: string;
  customerId: string;
  registration: string;
  make: string;
  model: string;
  year?: number;
  color?: string;
  vin?: string;
  mileage?: number;
  insuranceCompany?: string;
  insurancePolicy?: string;
  createdAt: number;
  updatedAt: number;
}

/**
 * Vehicle with owner information
 */
export interface VehicleWithOwner extends Vehicle {
  customer?: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
}

/**
 * Vehicle search parameters
 */
export interface VehicleSearchParams {
  customerId?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

/**
 * Create vehicle request data
 */
export interface CreateVehicleData {
  customerId: string;
  registration: string;
  make: string;
  model: string;
  year?: number;
  color?: string;
}

/**
 * Update vehicle request data
 */
export interface UpdateVehicleData {
  registration?: string;
  make?: string;
  model?: string;
  year?: number | null;
  color?: string | null;
}

/**
 * Vehicle list response from API
 */
interface VehicleListResponse {
  items: Vehicle[];
  limit: number;
  offset: number;
}

export interface VehicleActivity {
  type: 'booking' | 'service' | 'booking_event';
  id: string;
  date: string;
  title: string;
  description: string;
  status?: string;
  eventType?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Search/list vehicles with optional filters
 */
export async function list(params: VehicleSearchParams = {}): Promise<PaginatedResponse<Vehicle>> {
  const searchParams = new URLSearchParams();

  if (params.customerId) searchParams.set('customerId', params.customerId);
  if (params.q) searchParams.set('q', params.q);
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.offset) searchParams.set('offset', params.offset.toString());

  const queryString = searchParams.toString();
  const endpoint = queryString ? `/api/vehicles?${queryString}` : '/api/vehicles';

  const response = await api.get<VehicleListResponse>(endpoint);

  return {
    items: response.items,
    total: response.items.length + (response.offset || 0),
    limit: response.limit,
    offset: response.offset,
  };
}

/**
 * Get vehicle by ID with owner info
 */
export async function getById(id: string): Promise<VehicleWithOwner> {
  return api.get<VehicleWithOwner>(`/api/vehicles/${id}`);
}

/**
 * Get vehicle by registration
 */
export async function getByRegistration(registration: string): Promise<VehicleWithOwner> {
  return api.get<VehicleWithOwner>(`/api/vehicles/registration/${encodeURIComponent(registration)}`);
}

/**
 * Create a new vehicle
 */
export async function create(data: CreateVehicleData): Promise<Vehicle> {
  return api.post<Vehicle>('/api/vehicles', data);
}

/**
 * Update an existing vehicle
 */
export async function update(id: string, data: UpdateVehicleData): Promise<Vehicle> {
  return api.patch<Vehicle>(`/api/vehicles/${id}`, data);
}

/**
 * Delete a vehicle
 */
export async function remove(id: string): Promise<void> {
  await api.delete(`/api/vehicles/${id}`);
}

/**
 * Get vehicle activity timeline
 */
export async function getActivity(vehicleId: string, limit = 20): Promise<VehicleActivity[]> {
  return api.get<VehicleActivity[]>(
    `/api/vehicles/${vehicleId}/activity?limit=${encodeURIComponent(limit)}`
  );
}

export const vehiclesApi = {
  list,
  getById,
  getByRegistration,
  create,
  update,
  delete: remove,
  getActivity,
};
